import { httpClient } from '@/core/api/httpClient';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';
import type {
  ActiveTimerResponse,
  CreateManualEntryPayload,
  CreatedResponse,
  StopTimerResponse,
  TimeEntriesQueryParams,
  TimeEntryDtoPagedResult,
  UpdateTimeEntryPayload,
  UserTimeEntriesQueryParams,
} from '../types';
import {
  mockActiveTimer,
  mockCreatedResponse,
  mockProjectTimeEntries,
  mockStopTimerResponse,
  mockTaskTimeEntries,
  mockUserTimeEntries,
} from './time-tracking.mock';

interface TaskTimeEntriesParams {
  page?: number;
  pageSize?: number;
}

/** Maps camelCase query params to the backend's PascalCase param names. */
function toApiParams(params: UserTimeEntriesQueryParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (params.page !== undefined) out.Page = params.page;
  if (params.pageSize !== undefined) out.PageSize = params.pageSize;
  if (params.searchTerm) out.SearchTerm = params.searchTerm;
  if (params.sortBy) out.SortBy = params.sortBy;
  if (params.sortAscending !== undefined) out.SortAscending = params.sortAscending;
  if (params.from) out.From = params.from;
  if (params.to) out.To = params.to;
  return out;
}

export const timeTrackingService = {
  getActiveTimer: async (signal?: AbortSignal): Promise<ActiveTimerResponse> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockActiveTimer);
    const { data } = await httpClient.get<ActiveTimerResponse>(`${API_V1}/time/active`, { signal });
    return data;
  },

  startTimer: async (taskId: string): Promise<CreatedResponse> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockCreatedResponse);
    const { data } = await httpClient.post<object, CreatedResponse>(
      `${API_V1}/tasks/${taskId}/time/start`,
      {},
    );
    return data;
  },

  stopTimer: async (taskId: string): Promise<StopTimerResponse> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockStopTimerResponse);
    const { data } = await httpClient.post<object, StopTimerResponse>(
      `${API_V1}/tasks/${taskId}/time/stop`,
      {},
    );
    return data;
  },

  getTaskTimeEntries: async (
    taskId: string,
    params: TaskTimeEntriesParams = {},
    signal?: AbortSignal,
  ): Promise<TimeEntryDtoPagedResult> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockTaskTimeEntries);
    const { data } = await httpClient.get<TimeEntryDtoPagedResult>(
      `${API_V1}/tasks/${taskId}/time`,
      {
        params: toPascalCaseParams(params as Record<string, unknown>),
        signal,
      },
    );
    return data;
  },

  createManualEntry: async (
    taskId: string,
    payload: CreateManualEntryPayload,
  ): Promise<CreatedResponse> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockCreatedResponse);
    const { data } = await httpClient.post<CreateManualEntryPayload, CreatedResponse>(
      `${API_V1}/tasks/${taskId}/time`,
      payload,
    );
    return data;
  },

  updateTimeEntry: async (id: string, payload: UpdateTimeEntryPayload): Promise<void> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve();
    await httpClient.put<UpdateTimeEntryPayload, void>(`${API_V1}/time/${id}`, payload);
  },

  deleteTimeEntry: async (id: string): Promise<void> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve();
    await httpClient.delete<void>(`${API_V1}/time/${id}`);
  },

  getProjectTimeEntries: async (
    projectId: string,
    params: TimeEntriesQueryParams = {},
    signal?: AbortSignal,
  ): Promise<TimeEntryDtoPagedResult> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockProjectTimeEntries);
    const { data } = await httpClient.get<TimeEntryDtoPagedResult>(
      `${API_V1}/projects/${projectId}/time`,
      { params: toApiParams(params), signal },
    );
    return data;
  },

  getUserTimeEntries: async (
    userId: string,
    params: UserTimeEntriesQueryParams = {},
    signal?: AbortSignal,
  ): Promise<TimeEntryDtoPagedResult> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockUserTimeEntries);
    const { data } = await httpClient.get<TimeEntryDtoPagedResult>(
      `${API_V1}/users/${userId}/time`,
      {
        params: toApiParams(params),
        signal,
      },
    );
    return data;
  },
};
